import React from 'react';
import dayjs from 'dayjs';
import {useFormContext} from 'react-hook-form';
import ChekinProSettings from '../ChekinProSettings';
import {FIELD_TYPES, Guest, PhoneDetails, SelectOptionType} from '@guestapp/sdk';
import {
  FORM_NAMES,
  FormTypes,
  EXTRA_FORM_NAMES,
  buildGuestFormDataFromDB,
} from '../utils/guestForm';
import {usePreloadPoliceData} from 'components/GuestFormFields/usePreloadPoliceData';
import {buildGuestFormDataFromSDKPrefilling} from '../utils/sdk';
import {STORAGE_KEYS} from '../utils/constants';
import {SessionStorage} from '@guestapp/core';
import {useIVStore, getSetupFormData} from 'store/identityVerification';
import {buildSelectOption} from 'utils/common';

type UsePreloadGuestDataProps = {
  onPreloaded?: () => void;
  guestData?: Partial<Guest>;
};
function usePreloadGuestData({onPreloaded, guestData}: UsePreloadGuestDataProps = {}) {
  const isDataFromStoragePreloadedRef = React.useRef(false);
  const {reset, setValue, getValues} = useFormContext<FormTypes>();
  const {builtPhoneNumberString} = usePreloadPoliceData({
    getValues,
    reset,
  });
  const ivSetupData = useIVStore(getSetupFormData);

  const finishPreloadData = React.useCallback(
    (formData?: {
      [p: string]: string | Date | SelectOptionType | PhoneDetails | undefined;
    }) => {
      setValue(FORM_NAMES.name, formData?.[FORM_NAMES.name] as string, {
        shouldDirty: true,
      });
      reset({...getValues(), ...formData});
      isDataFromStoragePreloadedRef.current = true;
    },
    [reset, setValue, getValues],
  );

  const preloadPersistedFormData = React.useCallback(
    (guestId?: string) => {
      if (isDataFromStoragePreloadedRef.current) return;
      const storageFormData = SessionStorage.get<FormTypes>(
        STORAGE_KEYS.personalFormData,
      );
      const sdkPrefillingData = ChekinProSettings?.prefillData?.guestForm;

      if (guestId && guestData) {
        const formData = buildGuestFormDataFromDB({guestData});
        finishPreloadData(formData);
        SessionStorage.remove(STORAGE_KEYS.personalFormData);
        return;
      }
      if (!guestId && storageFormData && Object.keys(storageFormData)?.length) {
        const formData = Object.entries(storageFormData).reduce<
          Record<string, string | Date>
        >((acc, [fieldName, fieldValue]) => {
          if (fieldValue?.type === FIELD_TYPES.date) {
            acc[fieldName] = dayjs(fieldValue.value).toDate();
          } else {
            acc[fieldName] = fieldValue;
          }
          return acc;
        }, {});

        finishPreloadData({
          [FORM_NAMES.nationality]: buildSelectOption(ivSetupData.nationality),
          [FORM_NAMES.document_type]: buildSelectOption(ivSetupData.documentType?.value),
          ...formData,
          [FORM_NAMES.second_phone]: builtPhoneNumberString,
          [EXTRA_FORM_NAMES.secondPhoneDetails]:
            getValues()?.[EXTRA_FORM_NAMES.secondPhoneDetails],
        });
        onPreloaded?.();
        return;
      }

      if (sdkPrefillingData) {
        const formData = buildGuestFormDataFromSDKPrefilling({
          guestData: sdkPrefillingData,
        });
        finishPreloadData(formData);
      }
    },
    [
      guestData,
      finishPreloadData,
      onPreloaded,
      getValues,
      builtPhoneNumberString,
      ivSetupData,
    ],
  );

  const clearPersistedStorageFormData = () => {
    SessionStorage.clear();
  };

  const hasPersistedData = () => {
    const persistedData = SessionStorage.get<FormTypes>(STORAGE_KEYS.personalFormData);
    return Boolean(persistedData);
  };
  // const persistFormDataToStorage = React.useCallback(
  //   ({signature}: {signature: SignBlockRef | null}) => {
  //     const isSignatureTouched = signature?.isEnabled;
  //     const isFormTouched = formState.isDirty;
  //     const signatureUrl = signature?.canvas?.toDataURL();
  //
  //     if (formState.isSubmitSuccessful) return;
  //
  //     if (isSignatureTouched) {
  //       SessionStorage.set(STORAGE_KEYS.personalSignatureFormData, signatureUrl);
  //     }
  //     if (isFormTouched) {
  //       const formData = Object.entries(getValues()).reduce<Record<string, any>>(
  //         (acc, [fieldName, fieldValue]) => {
  //           if (fieldValue instanceof Date) {
  //             acc[fieldName] = {value: fieldValue, type: FIELD_TYPES.date};
  //           } else {
  //             acc[fieldName] = fieldValue;
  //           }
  //           return acc;
  //         },
  //         {},
  //       );
  //
  //       SessionStorage.set(STORAGE_KEYS.personalFormData, {
  //         ...formData,
  //       });
  //     }
  //   },
  //   [formState.isSubmitSuccessful, formState.isDirty, getValues],
  // );

  return {
    preloadPersistedFormData,
    clearPersistedStorageFormData,
    hasPersistedData,
    isPreloaded: isDataFromStoragePreloadedRef.current,
    // persistFormDataToStorage,
  };
}

export {usePreloadGuestData};
